import { useState, type ReactNode } from 'react';
import { useApi, Cargando, formatU, formatFecha, BotonAccion } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';
import { exportarCSV, exportarPDF, type ColumnaTabla } from '../shared/exportar.ts';

const hoy = new Date();
const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10);

const COL_MARGEN: ColumnaTabla[] = [
  { clave: 'total_cobro', etiqueta: 'Cobro', formato: 'moneda' },
  { clave: 'total_pago', etiqueta: 'Pago', formato: 'moneda' },
  { clave: 'margen', etiqueta: 'Margen', formato: 'moneda' },
  { clave: 'turnos', etiqueta: 'Turnos', formato: 'numero' },
];

const COL_HORAS: ColumnaTabla[] = [
  { clave: 'cliente_nombre', etiqueta: 'Cliente' },
  { clave: 'horas_total', etiqueta: 'Horas', formato: 'numero' },
  { clave: 'turnos', etiqueta: 'Turnos', formato: 'numero' },
];

const COL_RANKING: ColumnaTabla[] = [
  { clave: 'apellido', etiqueta: 'Apellido' },
  { clave: 'nombre', etiqueta: 'Nombre' },
  { clave: 'turnos', etiqueta: 'Turnos', formato: 'numero' },
  { clave: 'aceptadas', etiqueta: 'Acept.', formato: 'numero' },
  { clave: 'rechazadas', etiqueta: 'Rech.', formato: 'numero' },
  { clave: 'no_show', etiqueta: 'No show', formato: 'numero' },
  { clave: 'calificacion', etiqueta: 'Calif.', formato: 'numero' },
];

const COL_ASISTENCIA: ColumnaTabla[] = [
  { clave: 'fecha', etiqueta: 'Fecha', formato: 'fecha' },
  { clave: 'cliente_nombre', etiqueta: 'Local' },
  { clave: 'salon_nombre', etiqueta: 'Salón' },
  { clave: 'persona', etiqueta: 'Persona' },
  { clave: 'categoria_nombre', etiqueta: 'Puesto' },
  { clave: 'hora_prevista_inicio', etiqueta: 'Previsto' },
  { clave: 'llegada', etiqueta: 'Llegada' },
  { clave: 'salida', etiqueta: 'Salida' },
  { clave: 'rango', etiqueta: 'GPS' },
];

// Mismas columnas que la planilla semanal con la que factura el cliente.
const COL_RECIBO: ColumnaTabla[] = [
  { clave: 'fecha_str', etiqueta: 'FECHA' },
  { clave: 'documento', etiqueta: 'C.I.' },
  { clave: 'persona', etiqueta: 'NOMBRE' },
  { clave: 'entrada', etiqueta: 'HORA DE ENT.' },
  { clave: 'salida', etiqueta: 'HORA DE SAL.' },
  { clave: 'horas_str', etiqueta: 'HORAS' },
  { clave: 'salon_nombre', etiqueta: 'SALÓN' },
  { clave: 'categoria_nombre', etiqueta: 'FUNCIÓN' },
  { clave: 'cobro', etiqueta: 'COBRO', formato: 'moneda' },
];

interface ItemRecibo {
  convocatoria_id: string;
  solicitud_id: string;
  descripcion: string | null;
  fecha: string;
  documento: string | null;
  persona_nombre: string;
  persona_apellido: string;
  categoria_nombre: string;
  salon_nombre: string;
  hora_prevista_inicio: string;
  hora_prevista_fin: string;
  llegada_en: string | null;
  salida_en: string | null;
  hora_inicio_real: string | null;
  hora_fin_real: string | null;
  horas: number;
  cobro: number | null;
  estimado: boolean;
}

interface Recibo {
  cliente: { id: string; nombre: string };
  periodo: { desde: string; hasta: string };
  items: ItemRecibo[];
  subtotal: number;
  iva: number;
  iva_pct: number;
  total: number;
  hay_estimados: boolean;
}

/** Entrada/salida a mostrar: turno cargado → marcas GPS/QR → horario previsto. */
function entradaSalida(it: ItemRecibo): { ent: string; sal: string; origen: string } {
  if (it.hora_inicio_real && it.hora_fin_real) return { ent: it.hora_inicio_real, sal: it.hora_fin_real, origen: 'turno' };
  if (it.llegada_en) return { ent: horaLocal(it.llegada_en), sal: it.salida_en ? horaLocal(it.salida_en) : '—', origen: 'marcas' };
  return { ent: it.hora_prevista_inicio, sal: it.hora_prevista_fin, origen: 'previsto' };
}

/** Bloques por evento (solicitud), como la planilla del cliente. */
interface BloqueRecibo {
  solicitud_id: string;
  titulo: string;
  items: ItemRecibo[];
  subtotal: number;
}

function agruparPorEvento(items: ItemRecibo[]): BloqueRecibo[] {
  const bloques: BloqueRecibo[] = [];
  for (const it of items) {
    let b = bloques.length > 0 ? bloques[bloques.length - 1] : null;
    if (!b || b.solicitud_id !== it.solicitud_id) {
      const nombre = it.descripcion?.trim() ? it.descripcion.trim().toUpperCase() : `EVENTO ${formatFecha(it.fecha)}`;
      b = { solicitud_id: it.solicitud_id, titulo: `${nombre} · ${it.salon_nombre}`, items: [], subtotal: 0 };
      bloques.push(b);
    }
    b.items.push(it);
    b.subtotal += it.cobro ?? 0;
  }
  return bloques;
}

function lunesDeEstaSemana(): string {
  const d = new Date();
  const dow = d.getDay() === 0 ? 7 : d.getDay();
  const lun = new Date(d.getFullYear(), d.getMonth(), d.getDate() - (dow - 1));
  return `${lun.getFullYear()}-${String(lun.getMonth() + 1).padStart(2, '0')}-${String(lun.getDate()).padStart(2, '0')}`;
}

function domingoDeEstaSemana(): string {
  const [y, m, dd] = lunesDeEstaSemana().split('-').map(Number);
  const dom = new Date(y, m - 1, dd + 6);
  return `${dom.getFullYear()}-${String(dom.getMonth() + 1).padStart(2, '0')}-${String(dom.getDate()).padStart(2, '0')}`;
}

export function ReportesPage(): ReactNode {
  const [desde, setDesde] = useState(inicioMes);
  const [hasta, setHasta] = useState(finMes);

  // Recibo semanal por cliente (su propio rango: la semana en curso)
  const clientes = useApi<Array<{ id: string; nombre: string }>>('/api/clientes');
  const [reciboClienteId, setReciboClienteId] = useState('');
  const [reciboDesde, setReciboDesde] = useState(lunesDeEstaSemana());
  const [reciboHasta, setReciboHasta] = useState(domingoDeEstaSemana());
  const recibo = useApi<Recibo>(
    reciboClienteId ? `/api/reportes/recibo?cliente_id=${reciboClienteId}&desde=${reciboDesde}&hasta=${reciboHasta}` : null,
  );

  function exportarRecibo(fmt: 'csv' | 'pdf') {
    if (!recibo.datos || recibo.datos.items.length === 0) return;
    const r = recibo.datos;
    const vacia = { fecha_str: '', documento: '', persona: '', entrada: '', salida: '', horas_str: '', salon_nombre: '', categoria_nombre: '', cobro: null as number | null };
    const filas: Record<string, unknown>[] = [];
    for (const bloque of agruparPorEvento(r.items)) {
      // Encabezado del bloque, como en la planilla del cliente.
      filas.push({ ...vacia, fecha_str: bloque.titulo });
      for (const it of bloque.items) {
        const es = entradaSalida(it);
        filas.push({
          fecha_str: formatFecha(it.fecha),
          documento: it.documento ?? '',
          persona: `${it.persona_apellido} ${it.persona_nombre}`.toUpperCase(),
          entrada: es.ent,
          salida: es.sal,
          horas_str: it.horas.toLocaleString('es-UY'),
          salon_nombre: it.salon_nombre,
          categoria_nombre: it.categoria_nombre + (it.estimado ? ' (estimado)' : ''),
          cobro: it.cobro,
        });
      }
      const ivaBloque = Math.round(bloque.subtotal * (r.iva_pct / 100));
      filas.push(
        { ...vacia, categoria_nombre: 'SUBTOTAL', cobro: bloque.subtotal },
        { ...vacia, categoria_nombre: `IVA ${r.iva_pct}%`, cobro: ivaBloque },
        { ...vacia, categoria_nombre: 'TOTAL', cobro: bloque.subtotal + ivaBloque },
        { ...vacia },
      );
    }
    filas.push(
      { ...vacia, categoria_nombre: 'SUBTOTAL GENERAL', cobro: r.subtotal },
      { ...vacia, categoria_nombre: `IVA ${r.iva_pct}%`, cobro: r.iva },
      { ...vacia, categoria_nombre: 'TOTAL GENERAL', cobro: r.total },
    );
    const nombre = `recibo_${r.cliente.nombre.replace(/\s+/g, '-')}_${reciboDesde}_${reciboHasta}`;
    if (fmt === 'csv') exportarCSV(filas, COL_RECIBO, `${nombre}.csv`);
    else void exportarPDF(filas, COL_RECIBO, `Recibo ${r.cliente.nombre} · ${formatFecha(reciboDesde)} – ${formatFecha(reciboHasta)}`, `${nombre}.pdf`);
  }

  const margen = useApi<{ total_cobro: number; total_pago: number; margen: number; turnos: number }>(`/api/reportes/margen?desde=${desde}&hasta=${hasta}`);
  const horas = useApi<Array<{ cliente_id: string; cliente_nombre: string; horas_total: number; turnos: number }>>(`/api/reportes/horas-por-cliente?desde=${desde}&hasta=${hasta}`);
  const ranking = useApi<Array<{ persona_id: string; nombre: string; apellido: string; turnos: number; aceptadas: number; rechazadas: number; no_show: number; calificacion: number }>>(`/api/reportes/ranking-personal?desde=${desde}&hasta=${hasta}`);
  const asistencia = useApi<Array<{
    fecha: string; cliente_nombre: string; salon_nombre: string;
    persona_nombre: string; persona_apellido: string; categoria_nombre: string;
    hora_prevista_inicio: string; hora_prevista_fin: string;
    llegada_en: string | null; salida_en: string | null; metodo: string | null; fuera_de_rango: number;
  }>>(`/api/reportes/asistencia?desde=${desde}&hasta=${hasta}`);

  function exportarAsistencia(fmt: 'csv' | 'pdf') {
    if (!asistencia.datos || asistencia.datos.length === 0) return;
    const datos = asistencia.datos.map((a) => ({
      ...a,
      persona: `${a.persona_apellido}, ${a.persona_nombre}`,
      llegada: a.llegada_en ? horaLocal(a.llegada_en) : 'No marcó',
      salida: a.salida_en ? horaLocal(a.salida_en) : '',
      rango: a.llegada_en ? (a.fuera_de_rango ? 'Fuera de rango' : 'OK') : '',
    }));
    const nombre = `asistencia_${desde}_${hasta}`;
    if (fmt === 'csv') exportarCSV(datos, COL_ASISTENCIA, `${nombre}.csv`);
    else void exportarPDF(datos, COL_ASISTENCIA, `Asistencia ${formatFecha(desde)} – ${formatFecha(hasta)}`, `${nombre}.pdf`);
  }

  function exportarMargen(fmt: 'csv' | 'pdf') {
    if (!margen.datos) return;
    const d = [{ ...margen.datos }];
    const nombre = `margen_${desde}_${hasta}`;
    if (fmt === 'csv') exportarCSV(d, COL_MARGEN, `${nombre}.csv`);
    else void exportarPDF(d, COL_MARGEN, `Margen ${formatFecha(desde)} – ${formatFecha(hasta)}`, `${nombre}.pdf`);
  }

  function exportarHoras(fmt: 'csv' | 'pdf') {
    if (!horas.datos || horas.datos.length === 0) return;
    const nombre = `horas_cliente_${desde}_${hasta}`;
    if (fmt === 'csv') exportarCSV(horas.datos, COL_HORAS, `${nombre}.csv`);
    else void exportarPDF(horas.datos, COL_HORAS, `Horas por cliente ${formatFecha(desde)} – ${formatFecha(hasta)}`, `${nombre}.pdf`);
  }

  function exportarRanking(fmt: 'csv' | 'pdf') {
    if (!ranking.datos || ranking.datos.length === 0) return;
    const nombre = `ranking_personal_${desde}_${hasta}`;
    if (fmt === 'csv') exportarCSV(ranking.datos, COL_RANKING, `${nombre}.csv`);
    else void exportarPDF(ranking.datos, COL_RANKING, `Ranking de personal ${formatFecha(desde)} – ${formatFecha(hasta)}`, `${nombre}.pdf`);
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-neutro-800">Reportes</h2>

      {/* Filtro de período */}
      <div className="flex flex-wrap gap-2 rounded-2xl bg-white p-3 shadow-sm border border-contorno-var">
        <label className="text-sm flex items-center gap-2">
          <span className="text-neutro-400">Desde</span>
          <input className="campo w-36" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="text-sm flex items-center gap-2">
          <span className="text-neutro-400">Hasta</span>
          <input className="campo w-36" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <span className="text-xs text-neutro-400 self-center">{formatFecha(desde)} – {formatFecha(hasta)}</span>
      </div>

      {/* Margen */}
      <section className="rounded-2xl bg-primario-400 p-5 text-white shadow-sm">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold">Margen del período</h3>
          {margen.datos && (
            <div className="flex gap-1">
              <button onClick={() => exportarMargen('csv')} className="rounded px-2 py-0.5 text-xs text-white/80 hover:bg-white/10">CSV</button>
              <button onClick={() => exportarMargen('pdf')} className="rounded px-2 py-0.5 text-xs text-white/80 hover:bg-white/10">PDF</button>
            </div>
          )}
        </div>
        {margen.cargando ? <p className="text-white/70 text-sm">…</p> : margen.datos ? (
          <div className="grid grid-cols-4 gap-3 text-center text-sm">
            <div><p className="text-xl font-bold">{formatU(margen.datos.total_cobro)}</p><p className="text-white/70">Cobro</p></div>
            <div><p className="text-xl font-bold">{formatU(margen.datos.total_pago)}</p><p className="text-white/70">Pago</p></div>
            <div><p className="text-xl font-bold text-white">{formatU(margen.datos.margen)}</p><p className="text-white/70">Margen</p></div>
            <div><p className="text-xl font-bold">{margen.datos.turnos}</p><p className="text-white/70">Turnos</p></div>
          </div>
        ) : null}
      </section>

      {/* Recibo semanal por cliente (formato de la planilla de facturación) */}
      <section className="rounded-2xl bg-white p-5 shadow-sm border border-contorno-var">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold text-neutro-800">Recibo para el cliente</h3>
            <p className="text-xs text-neutro-400">Quién trabajó, entrada/salida, horas y cobro — listo para facturar</p>
          </div>
          {recibo.datos && recibo.datos.items.length > 0 && (
            <div className="flex gap-1">
              <BotonAccion variante="fantasma" onClick={() => exportarRecibo('csv')} className="text-xs">Excel (CSV)</BotonAccion>
              <BotonAccion variante="fantasma" onClick={() => exportarRecibo('pdf')} className="text-xs">PDF</BotonAccion>
            </div>
          )}
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <select className="campo w-full sm:w-60" value={reciboClienteId} onChange={(e) => setReciboClienteId(e.target.value)}>
            <option value="">Elegí un cliente…</option>
            {(clientes.datos ?? []).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
          <label className="flex items-center gap-2">
            <span className="text-neutro-400">Desde</span>
            <input className="campo w-36" type="date" value={reciboDesde} onChange={(e) => setReciboDesde(e.target.value)} />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-neutro-400">Hasta</span>
            <input className="campo w-36" type="date" value={reciboHasta} onChange={(e) => setReciboHasta(e.target.value)} />
          </label>
        </div>

        {!reciboClienteId ? (
          <p className="text-sm text-neutro-400">Elegí un cliente para armar el recibo de la semana.</p>
        ) : recibo.cargando ? <Cargando texto="…" /> : recibo.datos && recibo.datos.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-neutro-500">
                <tr>
                  <th className="px-2 py-1">Fecha</th>
                  <th className="px-2 py-1">C.I.</th>
                  <th className="px-2 py-1">Nombre</th>
                  <th className="px-2 py-1">Ent.</th>
                  <th className="px-2 py-1">Sal.</th>
                  <th className="px-2 py-1">Horas</th>
                  <th className="px-2 py-1">Salón</th>
                  <th className="px-2 py-1">Función</th>
                  <th className="px-2 py-1 text-right">Cobro</th>
                </tr>
              </thead>
              <tbody>
                {agruparPorEvento(recibo.datos.items).map((bloque) => (
                  <FragmentoBloque key={bloque.solicitud_id} bloque={bloque} ivaPct={recibo.datos!.iva_pct} />
                ))}
              </tbody>
              <tfoot className="font-bold text-neutro-800">
                <tr className="border-t-2 border-contorno-var">
                  <td colSpan={8} className="px-2 py-1.5 text-right">SUBTOTAL GENERAL</td>
                  <td className="px-2 py-1.5 text-right">{formatU(recibo.datos.subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan={8} className="px-2 py-1 text-right text-neutro-500">IVA {recibo.datos.iva_pct}%</td>
                  <td className="px-2 py-1 text-right text-neutro-500">{formatU(recibo.datos.iva)}</td>
                </tr>
                <tr>
                  <td colSpan={8} className="px-2 py-1.5 text-right text-base">TOTAL GENERAL</td>
                  <td className="px-2 py-1.5 text-right text-base text-primario-500">{formatU(recibo.datos.total)}</td>
                </tr>
              </tfoot>
            </table>
            {recibo.datos.hay_estimados && (
              <p className="mt-2 text-xs text-neutro-400">
                ≈ Cobro estimado con la tarifa vigente: falta cargar el turno real de ese servicio (desde Solicitudes → Cargar turno).
              </p>
            )}
          </div>
        ) : <p className="text-sm text-neutro-400">Sin servicios aceptados de este cliente en el período.</p>}
      </section>

      {/* Horas por cliente */}
      <section className="rounded-2xl bg-white p-5 shadow-sm border border-contorno-var">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold text-neutro-800">Horas por cliente</h3>
          {horas.datos && horas.datos.length > 0 && (
            <div className="flex gap-1">
              <BotonAccion variante="fantasma" onClick={() => exportarHoras('csv')} className="text-xs">CSV</BotonAccion>
              <BotonAccion variante="fantasma" onClick={() => exportarHoras('pdf')} className="text-xs">PDF</BotonAccion>
            </div>
          )}
        </div>
        {horas.cargando ? <Cargando texto="…" /> : horas.datos && horas.datos.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {horas.datos.map((h) => (
              <li key={h.cliente_id} className="flex items-center justify-between border-b border-contorno-var py-1.5">
                <span>{h.cliente_nombre}</span>
                <span className="text-neutro-500">{h.horas_total.toFixed(1)}h · {h.turnos} turnos</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-neutro-400">Sin datos para este período.</p>}
      </section>

      {/* Ranking de personal */}
      <section className="rounded-2xl bg-white p-5 shadow-sm border border-contorno-var">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold text-neutro-800">Ranking de personal</h3>
          {ranking.datos && ranking.datos.length > 0 && (
            <div className="flex gap-1">
              <BotonAccion variante="fantasma" onClick={() => exportarRanking('csv')} className="text-xs">CSV</BotonAccion>
              <BotonAccion variante="fantasma" onClick={() => exportarRanking('pdf')} className="text-xs">PDF</BotonAccion>
            </div>
          )}
        </div>
        {ranking.cargando ? <Cargando texto="…" /> : ranking.datos && ranking.datos.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-neutro-500">
                <tr>
                  <th className="px-2 py-1">Nombre</th>
                  <th className="px-2 py-1">Turnos</th>
                  <th className="px-2 py-1">Acept.</th>
                  <th className="px-2 py-1">Rech.</th>
                  <th className="px-2 py-1">No show</th>
                  <th className="px-2 py-1">Calif.</th>
                </tr>
              </thead>
              <tbody>
                {ranking.datos.map((p) => (
                  <tr key={p.persona_id} className="border-t border-contorno-var">
                    <td className="px-2 py-1.5 font-medium">{p.apellido}, {p.nombre}</td>
                    <td className="px-2 py-1.5">{p.turnos}</td>
                    <td className="px-2 py-1.5 text-estado-ok">{p.aceptadas}</td>
                    <td className="px-2 py-1.5">{p.rechazadas}</td>
                    <td className="px-2 py-1.5 text-estado-error">{p.no_show}</td>
                    <td className="px-2 py-1.5">{p.calificacion > 0 ? p.calificacion.toFixed(1) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-neutro-400">Sin datos para este período.</p>}
      </section>

      {/* Asistencia */}
      <section className="rounded-2xl bg-white p-5 shadow-sm border border-contorno-var">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold text-neutro-800">Asistencia (llegada y salida)</h3>
          {asistencia.datos && asistencia.datos.length > 0 && (
            <div className="flex gap-1">
              <BotonAccion variante="fantasma" onClick={() => exportarAsistencia('csv')} className="text-xs">CSV</BotonAccion>
              <BotonAccion variante="fantasma" onClick={() => exportarAsistencia('pdf')} className="text-xs">PDF</BotonAccion>
            </div>
          )}
        </div>
        {asistencia.cargando ? <Cargando texto="…" /> : asistencia.datos && asistencia.datos.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-neutro-500">
                <tr>
                  <th className="px-2 py-1">Fecha</th>
                  <th className="px-2 py-1">Local · Salón</th>
                  <th className="px-2 py-1">Persona</th>
                  <th className="px-2 py-1">Previsto</th>
                  <th className="px-2 py-1">Llegada</th>
                  <th className="px-2 py-1">Salida</th>
                  <th className="px-2 py-1">Método</th>
                </tr>
              </thead>
              <tbody>
                {asistencia.datos.map((a, i) => (
                  <tr key={i} className="border-t border-contorno-var">
                    <td className="px-2 py-1.5">{formatFecha(a.fecha)}</td>
                    <td className="px-2 py-1.5">{a.cliente_nombre} · {a.salon_nombre}</td>
                    <td className="px-2 py-1.5 font-medium">{a.persona_apellido}, {a.persona_nombre} <span className="text-xs text-neutro-400">({a.categoria_nombre})</span></td>
                    <td className="px-2 py-1.5 text-neutro-500">{formatoRango(a.hora_prevista_inicio, a.hora_prevista_fin)}</td>
                    <td className="px-2 py-1.5">
                      {a.llegada_en
                        ? <span className={a.fuera_de_rango ? 'text-estado-pendiente' : 'text-estado-ok'}>{horaLocal(a.llegada_en)}{a.fuera_de_rango ? ' ⚠' : ''}</span>
                        : <span className="font-medium text-estado-error">No marcó</span>}
                    </td>
                    <td className="px-2 py-1.5">{a.salida_en ? horaLocal(a.salida_en) : '—'}</td>
                    <td className="px-2 py-1.5 text-xs uppercase text-neutro-400">{a.llegada_en ? a.metodo : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-neutro-400">⚠ = marcó fuera del rango GPS del salón · "No marcó" = aceptó el servicio pero no registró llegada</p>
          </div>
        ) : <p className="text-sm text-neutro-400">Sin servicios aceptados en este período.</p>}
      </section>
    </div>
  );
}

function FragmentoBloque({ bloque, ivaPct }: { bloque: BloqueRecibo; ivaPct: number }): ReactNode {
  const iva = Math.round(bloque.subtotal * (ivaPct / 100));
  return (
    <>
      <tr className="border-t-2 border-contorno-var bg-superficie-100/60">
        <td colSpan={9} className="px-2 py-1.5 text-xs font-extrabold uppercase tracking-wide text-primario-500">{bloque.titulo}</td>
      </tr>
      {bloque.items.map((it) => {
        const es = entradaSalida(it);
        return (
          <tr key={it.convocatoria_id} className="border-t border-contorno-var">
            <td className="px-2 py-1.5">{formatFecha(it.fecha)}</td>
            <td className="px-2 py-1.5">{it.documento ?? '—'}</td>
            <td className="px-2 py-1.5 font-medium">{it.persona_apellido} {it.persona_nombre}</td>
            <td className="px-2 py-1.5">{es.ent}</td>
            <td className="px-2 py-1.5">{es.sal}</td>
            <td className="px-2 py-1.5">{it.horas.toLocaleString('es-UY')}</td>
            <td className="px-2 py-1.5">{it.salon_nombre}</td>
            <td className="px-2 py-1.5">{it.categoria_nombre}</td>
            <td className="px-2 py-1.5 text-right font-semibold">
              {it.cobro != null ? `${it.estimado ? '≈ ' : ''}${formatU(it.cobro)}` : 'Sin tarifa'}
            </td>
          </tr>
        );
      })}
      <tr className="border-t border-contorno-var text-xs text-neutro-500">
        <td colSpan={8} className="px-2 py-1 text-right">Subtotal {formatU(bloque.subtotal)} · IVA {ivaPct}% {formatU(iva)}</td>
        <td className="px-2 py-1 text-right font-bold text-neutro-700">{formatU(bloque.subtotal + iva)}</td>
      </tr>
    </>
  );
}

function horaLocal(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', hour12: false });
}